import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { cn } from "../lib/utils";
import { Icon, type IconSymbol } from "./icon";

export const baseButtonVariants = cva(
  "group inline-flex items-center h-6 justify-center whitespace-nowrap rounded transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-white border border-primary hover:bg-pink-600 hover:border-pink-600 disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-600",
        secondary:
          "bg-white text-red-700 border-pink-400 border border-solid hover:bg-pink-50 disabled:text-gray-500 disabled:border-gray-500",
        link: "text-primary hover:text-pink-600 disabled:text-gray-500",
        ghost:
          "bg-white text-gray-900 hover:bg-gray-100 disabled:text-gray-500",
        simple: "bg-transparent border-0 !p-0 h-auto hover:opacity-80",
      },
      size: {
        xs: "h-5.5 w-5.5",
        sm: "h-4 p-1",
        default: "h-6 p-2",
        xl: "h-7 p-3",
        xxl: "h-9 p-4",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

// Props communs pour les boutons
interface CommonButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof baseButtonVariants> {
  asChild?: boolean;
}

// Composant Button
export interface ButtonProps extends CommonButtonProps {
  leftIcon?: IconSymbol;
  rightIcon?: IconSymbol;
  loading?: boolean;
  childrenClassName?: string;
  ghostInTableCell?: boolean;
}

const Button = (props: ButtonProps) => {
  const {
    className,
    variant,
    size,
    loading = false,
    disabled,
    asChild,
    leftIcon,
    rightIcon,
    children,
    childrenClassName,
    ghostInTableCell,
    ...rest
  } = props;
  const Comp = asChild ? Slot : "button";

  const content = (
    <>
      {leftIcon && !loading && (
        <Icon icon={leftIcon} className={cn(variant === "link" && "pr-2")} />
      )}
      {loading && (
        <Icon
          className={cn(
            "animate-spin text-white group-disabled:text-gray-500",
            variant === "link" && "mr-2",
          )}
          size={16}
          strokeWidth={2}
          aria-hidden={true}
          icon="loader-circle"
        />
      )}
      <span
        className={cn(
          variant !== "link" && "px-2",
          "flex gap-2",
          childrenClassName,
        )}
      >
        {children}
      </span>
      {rightIcon && !loading && (
        <Icon icon={rightIcon} className={cn(variant === "link" && "pl-2")} />
      )}
    </>
  );

  const buttonContent = (
    <Comp
      className={cn(
        baseButtonVariants({ variant, size }),
        ghostInTableCell && "hover:bg-white",
        className,
      )}
      {...rest}
      disabled={loading || disabled}
    >
      {asChild ? (
        <div className="flex items-center justify-center">{content}</div>
      ) : (
        content
      )}
    </Comp>
  );

  return buttonContent;
};
Button.displayName = "Button";

// Composant IconButton
export interface IconButtonProps extends CommonButtonProps {
  icon?: IconSymbol;
  loading?: boolean;
  showCornerAccent?: boolean;
  size?: "xs" | "sm" | "default";
  ghostInTableCell?: boolean;
  "aria-label"?: string;
}

// Corner accent style
const cornerAccentStyles =
  "absolute -top-[4px] -right-[4px] w-2 h-2 rounded-sm";

const IconButton = (props: IconButtonProps) => {
  const {
    className,
    variant,
    size,
    asChild,
    icon,
    loading = false,
    disabled,
    showCornerAccent = false,
    ghostInTableCell,
    "aria-label": ariaLabel,
    ...rest
  } = props;
  const Comp = asChild ? Slot : "button";

  // Combine all content into a single element when using Slot
  const content = (
    <>
      {loading && (
        <Icon
          icon="loader-circle"
          className="animate-spin text-white group-disabled:text-gray-500"
          size={size === "xs" ? 16 : size === "sm" ? 12 : 16}
          strokeWidth={2}
          aria-hidden={true}
        />
      )}
      {icon && !loading && (
        <Icon icon={icon} size={size === "xs" ? 16 : size === "sm" ? 12 : 16} />
      )}
      {showCornerAccent && !loading && !disabled && (
        <span
          className={cn(
            cornerAccentStyles,
            size === "xs" && "-top-[2.5px] -right-[2.5px] w-1.5 h-1.5",
            size === "sm" && "-top-[3px] -right-[3px] w-1.5 h-1.5",
            variant === "primary"
              ? "bg-primary"
              : "bg-white border border-primary",
          )}
          aria-hidden="true"
        />
      )}
    </>
  );

  const buttonContent = (
    <Comp
      className={cn(
        baseButtonVariants({ variant, size, className }),
        ghostInTableCell && "hover:bg-white",
        "relative",
      )}
      {...rest}
      disabled={loading || disabled}
    >
      {asChild ? (
        <div className="relative flex items-center justify-center">
          {content}
        </div>
      ) : (
        content
      )}
    </Comp>
  );

  return buttonContent;
};
IconButton.displayName = "IconButton";

export { Button, IconButton };
